// LIBRERIE/IMPORTS
import React, { useCallback, useEffect } from 'react';
import ReactModal from 'react-modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

interface GalleryModalProps {
    isOpen: boolean;
    onClose: () => void;
    currentImage: {id:number, main:boolean, url:string|null};
    currentIndex: number;
    totalImages: number;
    onPrev: () => void;
    onNext: () => void;
    caption: string;
    closeButtonLabel: string;
    prevButtonLabel: string;
    nextButtonLabel: string;
    counterPattern: string;
    contentLabel: string;
    imageAltTextPattern?: string;
}

export const GalleryModal: React.FC<GalleryModalProps> = ({
    isOpen,
    onClose,
    currentImage,
    currentIndex,
    totalImages,
    onPrev,
    onNext,
    caption,
    closeButtonLabel,
    prevButtonLabel,
    nextButtonLabel,
    counterPattern,
    contentLabel,
    imageAltTextPattern
}) => {

    // --- KEYBOARD NAVIGATION HANDLER ---
    const handleKeyDown = useCallback((event: KeyboardEvent) => {
        if (!isOpen || totalImages <= 1) return;

        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            onPrev();
        } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            onNext();
        } else if (event.key === 'Escape') {
            event.preventDefault();
            onClose();
        }
    }, [isOpen, onPrev, onNext, onClose, totalImages]);

    // --- EFFECT PER AGGIUNGERE/RIMUOVERE EVENT LISTENER ---
    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
        } else {
            document.removeEventListener('keydown', handleKeyDown);
        } return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, handleKeyDown]);

    if (!currentImage || totalImages === 0) {
        return null;
    }

    const counterText = counterPattern
        .replace('{{current}}', String(currentIndex + 1))
        .replace('{{total}}', String(totalImages));

    let altForImage: string;
    if (imageAltTextPattern) {
        altForImage = imageAltTextPattern
            .replace('{{index}}', String(currentIndex + 1))
            .replace('{{total}}', String(totalImages))
            .replace('{{caption}}', caption);
    } else {
        altForImage = 'Immagine ingrandita';
    }
    return (
        <ReactModal
            isOpen={isOpen}
            onRequestClose={onClose}
            className="full-screen-modal"
            overlayClassName="modal-overlay"
            contentLabel={contentLabel}
            shouldCloseOnOverlayClick={true}
            shouldCloseOnEsc={true}
            ariaHideApp={false}
        >
            {/* Pulsante Chiudi */}
            <button
                className="close-button"
                onClick={onClose}
                aria-label={closeButtonLabel}
                title={closeButtonLabel}
            >
                <FontAwesomeIcon icon={faClose} size="lg" />
            </button>

            {/* Navigazione */}
            {totalImages > 1 && (
                <>
                    <button
                        className="nav-button left"
                        onClick={onPrev}
                        aria-label={prevButtonLabel}
                        title={prevButtonLabel}
                        style={{
                            width: "60px",
                            height: "60px",
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                        }}
                    >
                        <FontAwesomeIcon icon={faChevronLeft} size="2x" />
                    </button>

                    <button
                        className="nav-button right"
                        onClick={onNext}
                        aria-label={nextButtonLabel}
                        title={nextButtonLabel}
                        style={{
                            width: "60px",
                            height: "60px",
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                        }}
                    >
                        <FontAwesomeIcon icon={faChevronRight} size="2x" />
                    </button>
                </>
            )}

            {/* Immagine Principale */}
            <img
                src={currentImage.url ?? '/assets/images/no_image_big.png'}
                alt={altForImage}
                className="modal-img"
            />

            {/* Barra Inferiore con Caption e Contatore */}
            <figcaption className="mfp-bottom-bar" style={{ paddingLeft: "1%" }}>
                {caption && <div className="mfp-title">{caption}</div>}
                {totalImages > 0 && <div className="mfp-counter" style={{ paddingRight: "1%" }}>{counterText}</div>}
            </figcaption>
        </ReactModal>
    )
};